import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, HardDrive, Radio, Sparkles, UserPlus } from 'lucide-react';
import { useT, useLang } from '../lib/i18n';
import { useMediaList, useOverview } from '../lib/live';
import { ago, bytes, hours, rttTone } from '../lib/format';
import { avatarUrl } from '../lib/api';
import { useIsHost } from '../lib/session';
import { Avatar, CountUp, Dot, PipelineBar, Segmented, StatTile } from '../ui';
import { useEnter } from '../motion/gsap';
import { MediaTable, StorageCard } from './parts';
import type { Overview } from '../lib/types';
import { joinRoom, watchTogether } from '../features/room/session';

export function OverviewPage() {
  const ov = useOverview((s) => s.data);
  const isHost = useIsHost();
  const nav = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  useEnter(ref, [!!ov]);
  if (!ov) return null;
  return (
    <div className="grid-main" ref={ref}>
      <div className="stack">
        {isHost && !!ov.pendingPeople && (
          <div className="banner" data-anim>
            <UserPlus size={16} />
            {ov.pendingPeople === 1 ? '1 person signed up and is waiting to join the team.' : `${ov.pendingPeople} people signed up and are waiting to join the team.`}
            <span className="spacer" />
            <button className="btn btn-black btn-sm" onClick={() => nav('/team')}>
              Review
            </button>
          </div>
        )}
        <DriveCard ov={ov} />
        <LibraryCard />
      </div>
      <div className="stack">
        <BridgeCard ov={ov} />
        <StorageCard ov={ov} />
      </div>
    </div>
  );
}

function DriveCard({ ov }: { ov: Overview }) {
  const t = useT();
  const nav = useNavigate();
  const [tab, setTab] = useState<'library' | 'network'>('library');
  const p = ov.pipeline;
  const encoding = p.probing + p.remuxing + p.transcoding;
  const processing = encoding + p.queued;
  const peers = (ov.live ?? []).flatMap((r) => r.peers);
  const slowest = peers.reduce<(typeof peers)[number] | null>((a, b) => (!a || b.rtt > a.rtt ? b : a), null);
  const meanDrift = peers.length ? peers.reduce((a, b) => a + Math.abs(b.drift), 0) / peers.length : 0;
  const wt = peers.filter((x) => x.transport === 'webtransport').length;

  return (
    <section className="card" data-anim>
      <div className="card-head">
        <div style={{ flex: 1 }}>
          <h2 className="card-title">{t('ov.drive')}</h2>
          <div className="card-sub">
            <HardDrive size={13} />
            <span>
              {ov.drive.name} · <span className="mono">{ov.drive.encoder}</span>
            </span>
            {processing > 0 ? <span className="chip chip-amber">{t('chip.processing', { n: processing })}</span> : <span className="chip chip-accent">{t('chip.allReady')}</span>}
          </div>
        </div>
        <Segmented
          value={tab}
          onChange={setTab}
          options={[
            { value: 'library', label: t('ov.library') },
            { value: 'network', label: t('ov.network') },
          ]}
        />
      </div>

      {tab === 'library' ? (
        <div className="stats">
          <StatTile dark label={t('ov.titles')} value={<CountUp value={ov.library.total} />} sub={t('ov.hoursOfVideo', { h: hours(ov.library.hours) })} />
          <StatTile label={t('ov.ready')} value={<CountUp value={ov.library.playable} />} sub={t('ov.failed', { n: p.failed })} />
          <StatTile label={t('ov.watching')} value={<CountUp value={ov.online} />} sub={t('ov.inRooms', { n: ov.live?.length ?? 0 })} />
          <StatTile
            label="Space left"
            value={
              <>
                <CountUp value={ov.drive.available / 1e9} format={(n) => (n >= 1000 ? (n / 1000).toFixed(2) : Math.round(n).toString())} />
                <small>{ov.drive.available >= 1e12 ? 'TB' : 'GB'}</small>
              </>
            }
            sub={ov.drive.quota > 0 ? `of a ${bytes(ov.drive.quota, 0)} library limit` : `${bytes(ov.drive.free, 1)} free · ${bytes(ov.drive.reserve, 0)} kept free for the system`}
          />
        </div>
      ) : (
        <div className="stats">
          <StatTile
            dark
            label="Slowest link"
            value={
              <>
                <CountUp value={slowest?.rtt ?? 0} />
                <small>ms</small>
              </>
            }
            sub={slowest ? slowest.name : 'nobody watching'}
          />
          <StatTile
            label="Sync drift"
            value={
              <>
                ±<CountUp value={meanDrift} />
                <small>ms</small>
              </>
            }
            sub="mean |drift| across viewers"
          />
          <StatTile label={t('ov.transport')} value={<>{peers.length ? `${wt}/${peers.length}` : '—'}</>} sub="on WebTransport" />
          <StatTile label={t('nav.team')} value={<CountUp value={ov.members} />} sub={t('ov.membersSub', { n: ov.rooms })} />
        </div>
      )}

      <div className="pipeline">
        <div className="pipeline-head">
          <b>{t('ov.pipeline')}</b>
          <button className="pipeline-link" onClick={() => nav('/drive')}>
            {t('ov.openDrive')} <ChevronRight size={14} />
          </button>
        </div>
        <PipelineBar
          segments={[
            { key: 'up', label: t('pipe.uploading'), count: p.uploading, color: 'var(--seg-1)' },
            { key: 'q', label: t('pipe.queued'), count: p.queued, color: 'var(--beige)' },
            { key: 'enc', label: t('pipe.encoding'), count: encoding, color: 'var(--accent)' },
            { key: 'fail', label: t('pipe.failed'), count: p.failed, color: 'var(--seg-2)' },
            { key: 'ready', label: t('pipe.ready'), count: p.ready, color: 'var(--black)' },
          ]}
        />
      </div>
    </section>
  );
}

function LibraryCard() {
  const t = useT();
  const lang = useLang((s) => s.lang);
  const nav = useNavigate();
  const items = useMediaList((s) => s.items);
  const [mode, setMode] = useState<'latest' | 'all'>('latest');
  const rows = mode === 'latest' ? items.slice(0, 6) : items;
  const updated = items.reduce((a, m) => (m.updatedAt > a ? m.updatedAt : a), '');
  return (
    <section className="card" data-anim>
      <div className="card-head">
        <div style={{ flex: 1 }}>
          <h2 className="card-title">{t('ov.recent')}</h2>
          <div className="card-sub">{t('ov.recentSub', { n: items.length, ago: updated ? ago(updated, lang) : '—' })}</div>
        </div>
        <Segmented
          value={mode}
          onChange={setMode}
          options={[
            { value: 'latest', label: t('ov.latest') },
            { value: 'all', label: t('ov.all') },
          ]}
        />
      </div>
      <MediaTable rows={rows} onOpen={(m) => (m.playable ? watchTogether(m, nav) : nav('/drive'))} />
    </section>
  );
}

function BridgeCard({ ov }: { ov: Overview }) {
  const t = useT();
  const nav = useNavigate();
  const rooms = [...(ov.live ?? [])].sort((a, b) => b.peers.length - a.peers.length);
  const room = rooms[0];
  return (
    <section className="card card-dark" data-anim>
      <div className="insight-head">
        <Sparkles size={15} className="accent-ic" />
        {t('bridge.live')}
        <span className="mono">{room ? `${ov.online} watching` : 'idle'}</span>
      </div>
      {room ? (
        <>
          <h3 className="insight-title">{room.media ?? room.name}</h3>
          <ul className="insight-list">
            {room.peers.map((p) => (
              <li key={p.id}>
                <Avatar name={p.name} src={avatarUrl(p)} dark />
                <span className="truncate">{p.name}</span>
                <Dot tone={rttTone(p.rtt)} />
                <span className="mono" style={{ marginLeft: 0 }}>
                  {p.rtt ? `${Math.round(p.rtt)} ms` : '…'}
                </span>
              </li>
            ))}
          </ul>
          {rooms.length > 1 && <p className="insight-note">+{rooms.length - 1} more live room{rooms.length > 2 ? 's' : ''}</p>}
          <p className="insight-note">{t('bridge.note')}</p>
          <button className="btn btn-accent btn-sm" onClick={() => joinRoom(room.id, nav)}>
            <Radio size={14} />
            {t('btn.join')}
          </button>
        </>
      ) : (
        <>
          <h3 className="insight-title">{t('bridge.empty')}</h3>
          <p className="insight-note" style={{ fontSize: 12.5, lineHeight: 1.5, marginBottom: 0 }}>
            {t('bridge.emptySub')}
          </p>
        </>
      )}
    </section>
  );
}
